import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Archive, CheckCircle2, Database, HardDrive, HeartPulse, Radio, RefreshCw, Server, Timer,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { LogbookServerChip } from '../components/logbook/LogbookServerChip';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogbookServers } from '../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canAdministerLogbook } from '../utils/logbookPermissions';
import { formatByteSize, formatCount, relativeTime } from '../utils/logbook';
import { formatLocalDateTime } from '../utils/timestamps';
import { cn } from '../utils/cn';
import type { LogbookAdminStatus, LogbookCapabilities } from '../types/logbook';

/**
 * Whether the evidence can be trusted right now.
 *
 * Three questions decide that, and they are different questions. Is the service reachable and which
 * features does it actually have. Is each server's evidence arriving, or is a producer sitting on a
 * local backlog. And is the store healthy enough to keep accepting evidence, given retention, the
 * storage budget and the maintenance pass.
 *
 * Service settings are read-only here on purpose. Changing retention or enabling rollups discards
 * evidence across every server at once, which belongs with the operator holding the admin
 * credential rather than with a panel a moderator has open beside a case.
 */

/** A server that has not reported for this long is worth flagging rather than assuming is quiet. */
const STALE_MS = 6 * 60 * 60 * 1000;

export function LogbookHealthView() {
  useDocumentTitle('Logbook health');
  const { identity } = useAuth();
  const canAdminister = canAdministerLogbook(identity);
  const { servers, loading: serversLoading, error: serversError, reload } = useLogbookServers();

  const [capabilities, setCapabilities] = useState<LogbookCapabilities | null>(null);
  const [status, setStatus] = useState<LogbookAdminStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);

    const run = async () => {
      const [caps, adminStatus] = await Promise.all([
        logbookApiService.getCapabilities(controller.signal).catch(() => null),
        canAdminister
          ? logbookApiService.getAdminStatus(controller.signal).catch((caught) => {
            if (!cancelled) setStatusError(caught instanceof LogbookApiError ? caught.message : 'Service status is unavailable.');
            return null;
          })
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      setCapabilities(caps);
      setStatus(adminStatus);
      setLoading(false);
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [canAdminister, nonce]);

  const receipts = useMemo(() => {
    const now = Date.now();
    const reporting = servers.filter((server) => server.lastReceivedAt != null && !server.archived);
    const stale = reporting.filter((server) => now - (server.lastReceivedAt ?? 0) > STALE_MS);
    const silent = servers.filter((server) => server.lastReceivedAt == null && !server.archived);
    return { reporting, stale, silent, archived: servers.filter((server) => server.archived) };
  }, [servers]);

  const storage = useMemo(() => {
    if (!status || status.max_bytes <= 0) return null;
    const used = status.logical_used_bytes_including_wal;
    return { used, max: status.max_bytes, percent: Math.min(100, Math.round((used / status.max_bytes) * 100)) };
  }, [status]);

  const refresh = () => { setNonce((value) => value + 1); reload(); };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Evidence health"
        description="Whether Logbook is reachable, whether evidence is arriving, and how much is retained."
        icon={HeartPulse}
        backTo="/logbook"
        backLabel="Back to Logbook"
        titleAdornment={capabilities ? <span className="badge badge-neutral font-mono">{capabilities.version}</span> : undefined}
        actions={(
          <button type="button" className="glass-button" onClick={refresh} disabled={loading}>
            <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
            Refresh
          </button>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Servers reporting"
          value={formatCount(receipts.reporting.length)}
          icon={Server}
          tone="success"
          loading={serversLoading}
          hint={`of ${formatCount(servers.length)} configured`}
        />
        <StatTile
          label="Stale over 6h"
          value={formatCount(receipts.stale.length)}
          icon={Timer}
          tone={receipts.stale.length > 0 ? 'warning' : 'neutral'}
          loading={serversLoading}
        />
        <StatTile
          label="No evidence yet"
          value={formatCount(receipts.silent.length)}
          icon={Radio}
          tone={receipts.silent.length > 0 ? 'orange' : 'neutral'}
          loading={serversLoading}
        />
        <StatTile
          label="Retention"
          value={status ? `${formatCount(status.retention_days)}d` : canAdminister ? 'Unknown' : 'Restricted'}
          icon={Archive}
          tone="info"
          loading={loading && canAdminister}
          hint={status?.rollups.enabled ? `Position telemetry compacted after ${status.rollups.telemetry_raw_days}d` : 'Rollups off'}
        />
      </div>

      {serversError && (
        <div className="alert alert-danger">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{serversError}</span>
        </div>
      )}

      {/* Capability negotiation: what this build of the service actually offers */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Service contract" icon={Database} />
        {loading && !capabilities ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }, (_, index) => <SkeletonLoader key={index} className="h-12 w-full" />)}
          </div>
        ) : !capabilities ? (
          <EmptyState bare compact icon={Database} title="Capabilities unavailable" description="The service did not answer a capability read." />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'API version', value: `v${capabilities.api}` },
                { label: 'Event schema', value: String(capabilities.event_schema) },
                { label: 'Page ceiling', value: `${formatCount(capabilities.limits.page)} records` },
                { label: 'Query budget', value: `${capabilities.limits.query_seconds}s` },
              ].map((item) => (
                <div key={item.label}>
                  <div className="eyebrow">{item.label}</div>
                  <div className="text-sm text-white tabular-nums mt-0.5">{item.value}</div>
                </div>
              ))}
            </div>

            {capabilities.features.length > 0 && (
              <div>
                <div className="eyebrow mb-1.5">Available</div>
                <div className="flex flex-wrap gap-1.5">
                  {capabilities.features.map((feature) => (
                    <span key={feature} className="chip gap-1.5 font-mono text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {capabilities.unavailable.length > 0 && (
              <div>
                <div className="eyebrow mb-1.5">Not available on this deployment</div>
                <div className="flex flex-wrap gap-1.5">
                  {capabilities.unavailable.map((feature) => (
                    <span key={feature} className="chip gap-1.5 font-mono text-xs text-glass-400">
                      <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--tone-warning-text)' }} />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-server freshness */}
      <div className="glass-card overflow-hidden">
        <div className="p-4 md:p-5 pb-3">
          <SectionHeader title="Evidence arriving per server" icon={Server} count={servers.length} />
        </div>
        {serversLoading && servers.length === 0 ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }, (_, index) => <SkeletonLoader key={index} className="h-10 w-full" />)}
          </div>
        ) : servers.length === 0 ? (
          <div className="px-4 pb-6">
            <EmptyState bare compact icon={Server} title="No servers configured" />
          </div>
        ) : (
          <div className="table-wrap border-0 rounded-none">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Server</th>
                  <th className="th">Cluster</th>
                  <th className="th">Live</th>
                  <th className="th text-right">Last evidence</th>
                </tr>
              </thead>
              <tbody>
                {servers.map((server) => {
                  const stale = server.lastReceivedAt != null && Date.now() - server.lastReceivedAt > STALE_MS;
                  const silent = server.lastReceivedAt == null;
                  return (
                    <tr key={server.serverId}>
                      <td className="td">
                        <LogbookServerChip serverId={server.serverId} server={server} detailed />
                        <div className="text-xs text-glass-500 font-mono mt-0.5 break-all">{server.serverId}</div>
                      </td>
                      <td className="td text-glass-300">{server.cluster ?? <span className="text-glass-500">Not set</span>}</td>
                      <td className="td">
                        {server.archived ? (
                          <span className="badge badge-neutral">Archived</span>
                        ) : server.pack?.online ? (
                          <span className="badge badge-success badge-dot tabular-nums">{server.pack.players} online</span>
                        ) : server.pack ? (
                          <span className="badge badge-neutral">Offline</span>
                        ) : (
                          <span className="text-xs text-glass-500">Not in the pack list</span>
                        )}
                      </td>
                      <td className="td text-right">
                        {silent ? (
                          <span className="badge badge-orange">None yet</span>
                        ) : (
                          <span
                            className={cn('tabular-nums text-sm', stale ? 'text-yellow-300' : 'text-glass-300')}
                            title={formatLocalDateTime(new Date(server.lastReceivedAt!))}
                          >
                            {relativeTime(server.lastReceivedAt!)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Store health, for operators */}
      {canAdminister && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Store" icon={HardDrive} description="Read only." />
          {statusError ? (
            <EmptyState bare compact icon={AlertTriangle} tone="warning" title="Service status unavailable" description={statusError} />
          ) : loading && !status ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }, (_, index) => <SkeletonLoader key={index} className="h-12 w-full" />)}
            </div>
          ) : !status ? null : (
            <div className="space-y-4">
              {storage && (
                <div>
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <span className="eyebrow">Storage budget</span>
                    <span className="text-xs tabular-nums text-glass-400">
                      {formatByteSize(storage.used)} of {formatByteSize(storage.max)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--gauge-track)' }}>
                    <div
                      className={cn('h-full rounded-full transition-all', storage.percent >= 90 ? 'bg-red-500/80' : storage.percent >= 75 ? 'bg-yellow-500/80' : 'bg-accent-500/80')}
                      style={{ width: `${Math.max(2, storage.percent)}%` }}
                    />
                  </div>
                  <div className="mt-1 text-xs text-glass-500 tabular-nums">
                    {formatByteSize(status.reusable_database_bytes)} reusable inside {formatByteSize(status.database_allocated_bytes)} allocated.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Durability', value: status.durability },
                  { label: 'Retention', value: `${formatCount(status.retention_days)} days` },
                  { label: 'Rollups', value: status.rollups.enabled ? `On, raw kept ${status.rollups.telemetry_raw_days}d` : 'Off' },
                  { label: 'Jobs retained', value: formatCount(status.jobs_retained) },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="eyebrow">{item.label}</div>
                    <div className="text-sm text-white mt-0.5 break-words">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="subcard p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="eyebrow">Maintenance</div>
                  <div className="mt-0.5">
                    <span className={cn('badge', status.maintenance.state === 'degraded' ? 'badge-danger' : status.maintenance.state === 'ready' ? 'badge-success' : 'badge-neutral')}>
                      {status.maintenance.state}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Last pass</div>
                  <div className="text-sm text-glass-300 mt-0.5 tabular-nums">
                    {status.maintenance.last_completed_at ? relativeTime(status.maintenance.last_completed_at) : 'Not yet run'}
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Last pass work</div>
                  <div className="text-sm text-glass-300 mt-0.5 tabular-nums">
                    {formatCount(status.maintenance.rolled_up)} rolled up, {formatCount(status.maintenance.pruned)} pruned
                  </div>
                </div>
              </div>

              <div>
                <div className="eyebrow mb-1">Store generation</div>
                <code className="text-xs font-mono text-glass-400 break-all">{status.store_generation}</code>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
